/**
 * Rodapé de status (34 px) do layout A + C.
 *
 * `<StatusBar items updatedAt what approvals approvalsTo />`
 * - `items`: `StatusItem[]` (texto curto com ponto de estado `tone` opcional e rota `to` opcional);
 * - `updatedAt` + `what`: "métricas há 18 s" (sem `what`, "dados há 18 s"), atualizado sozinho;
 * - `approvals`: quantas ações aguardam aprovação (link à direita, para `approvalsTo`); 0 ou nada esconde.
 *
 * As telas não desenham o rodapé: alimentam o da casca com `useStatusBar` (shellSlots.tsx).
 * Sem região viva de propósito: o "há N s" muda a cada segundo e não deve ser anunciado.
 */
import { useI18n, type CommonKey } from '@hive/i18n';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { cx } from './layout';
import type { StatusFeed, StatusItem } from './shellSlots';

/** "18 s", "3 min", "2 h", "4 d": idade curta, igual nos três idiomas. */
export function shortAge(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h`;
  return `${Math.floor(h / 24)} d`;
}

/** Hora atual que se atualiza: a cada segundo no primeiro minuto, depois a cada 30 s. */
function useAge(at: StatusFeed['updatedAt']): number | null {
  const t = at == null ? null : new Date(at).getTime();
  const [now, setNow] = useState(() => Date.now());
  const age = t == null || Number.isNaN(t) ? null : now - t;
  const step = age != null && age < 60_000 ? 1000 : 30_000;
  useEffect(() => {
    if (t == null) return;
    const id = window.setInterval(() => setNow(Date.now()), step);
    return () => window.clearInterval(id);
  }, [t, step]);
  return age;
}

export function StatusBar({
  items = [],
  updatedAt,
  what,
  approvals,
  approvalsTo = '/decisions',
  className,
}: {
  items?: StatusItem[];
  updatedAt?: StatusFeed['updatedAt'];
  what?: string;
  approvals?: number;
  approvalsTo?: string;
  className?: string;
}) {
  const { t } = useI18n<CommonKey>();
  const age = useAge(updatedAt);
  return (
    <div className={cx('statusbar', className)} aria-label={t('ui.status.label')} role="group">
      {items.map((i, n) => {
        const body = (
          <>
            {i.tone && <span className={`sb-dot sb-${i.tone}`} aria-hidden="true" />}
            {i.label}
          </>
        );
        return i.to ? (
          <Link key={i.id ?? n} to={i.to} className="sb-item">
            {body}
          </Link>
        ) : (
          <span key={i.id ?? n} className="sb-item">
            {body}
          </span>
        );
      })}
      {age != null && <span className="sb-item sb-age">{what ? t('ui.status.updatedWhat', { what, age: shortAge(age) }) : t('ui.status.updated', { age: shortAge(age) })}</span>}
      {approvals ? (
        <Link to={approvalsTo} className="sb-approvals">
          {t('ui.status.approvals', { count: approvals })}
        </Link>
      ) : null}
    </div>
  );
}
